import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import type { PackageDraft, PlanDraft } from './api';
import { usePrepaidDeps } from './context';
import type { Credit, Membership, MembershipPlan, PrepaidPackage, Wallet } from './schemas';

/**
 * The TanStack Query layer.
 *
 * **A wallet is invalidated whenever anything touches money.** Selling a
 * package, taking a top-up, correcting a ledger and refunding all change
 * what a client holds, and a screen showing yesterday's balance beside
 * today's history is worse than one that simply refetches.
 */
export const prepaidKeys = {
  all: ['prepaid'] as const,
  packages: (includeInactive: boolean) =>
    [...prepaidKeys.all, 'packages', includeInactive] as const,
  plans: (includeInactive: boolean) => [...prepaidKeys.all, 'plans', includeInactive] as const,
  client: (clientId: string) => [...prepaidKeys.all, 'client', clientId] as const,
  entitlements: (invoiceId: string) => [...prepaidKeys.all, 'entitlements', invoiceId] as const,
  mine: () => [...prepaidKeys.all, 'mine'] as const,
};

export function usePackages(includeInactive = false): UseQueryResult<PrepaidPackage[]> {
  const { api } = usePrepaidDeps();
  return useQuery({
    queryKey: prepaidKeys.packages(includeInactive),
    queryFn: () => api.listPackages(includeInactive),
  });
}

export function usePlans(includeInactive = false): UseQueryResult<MembershipPlan[]> {
  const { api } = usePrepaidDeps();
  return useQuery({
    queryKey: prepaidKeys.plans(includeInactive),
    queryFn: () => api.listPlans(includeInactive),
  });
}

export function useCreatePackage(): UseMutationResult<PrepaidPackage, Error, PackageDraft> {
  const { api } = usePrepaidDeps();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PackageDraft) => api.createPackage(body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: prepaidKeys.all });
    },
  });
}

export function useCreatePlan(): UseMutationResult<MembershipPlan, Error, PlanDraft> {
  const { api } = usePrepaidDeps();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PlanDraft) => api.createPlan(body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: prepaidKeys.all });
    },
  });
}

export function useSetPackageActive(): UseMutationResult<
  PrepaidPackage,
  Error,
  { packageId: string; active: boolean }
> {
  const { api } = usePrepaidDeps();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ packageId, active }) => api.setPackageActive(packageId, active),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: prepaidKeys.all });
    },
  });
}

/** Everything one client holds: credits, membership, wallet. */
export function useClientCredits(clientId: string): UseQueryResult<Credit[]> {
  const { api } = usePrepaidDeps();
  return useQuery({
    queryKey: [...prepaidKeys.client(clientId), 'credits'],
    queryFn: () => api.listCredits(clientId),
    enabled: clientId !== '',
  });
}

export function useClientMembership(clientId: string): UseQueryResult<Membership | null> {
  const { api } = usePrepaidDeps();
  return useQuery({
    queryKey: [...prepaidKeys.client(clientId), 'membership'],
    queryFn: () => api.readMembership(clientId),
    enabled: clientId !== '',
  });
}

export function useClientWallet(clientId: string): UseQueryResult<Wallet> {
  const { api } = usePrepaidDeps();
  return useQuery({
    queryKey: [...prepaidKeys.client(clientId), 'wallet'],
    queryFn: () => api.readWallet(clientId),
    enabled: clientId !== '',
  });
}

/**
 * Anything that moves one client's money invalidates all three of their
 * reads, because all three can change together: a package sale takes money
 * and creates credits, and a refund closes a purchase and credits a wallet.
 */
function useClientWrite<TData, TVars>(
  clientId: string,
  mutationFn: (vars: TVars) => Promise<TData>,
): UseMutationResult<TData, Error, TVars> {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: prepaidKeys.client(clientId) });
    },
  });
}

export function useSellPackage(
  clientId: string,
): UseMutationResult<unknown, Error, { packageId: string }> {
  const { api, outletId } = usePrepaidDeps();
  return useClientWrite(clientId, ({ packageId }: { packageId: string }) =>
    api.sellPackage(clientId, packageId, outletId),
  );
}

export function useSellMembership(
  clientId: string,
): UseMutationResult<unknown, Error, { planId: string }> {
  const { api, outletId } = usePrepaidDeps();
  return useClientWrite(clientId, ({ planId }: { planId: string }) =>
    api.sellMembership(clientId, planId, outletId),
  );
}

export function useTopUp(
  clientId: string,
): UseMutationResult<{ amount_paise: number; bonus_paise: number }, Error, number> {
  const { api, outletId } = usePrepaidDeps();
  return useClientWrite(clientId, (amountPaise: number) =>
    api.topUp(clientId, amountPaise, outletId),
  );
}

export function useAdjustWallet(
  clientId: string,
): UseMutationResult<unknown, Error, { amountPaise: number; reason: string }> {
  const { api } = usePrepaidDeps();
  return useClientWrite(
    clientId,
    ({ amountPaise, reason }: { amountPaise: number; reason: string }) =>
      api.adjust(clientId, amountPaise, reason),
  );
}

export function useRefundPackage(
  clientId: string,
): UseMutationResult<{ amount_paise: number }, Error, string> {
  const { api } = usePrepaidDeps();
  return useClientWrite(clientId, (purchaseId: string) => api.refundPackage(clientId, purchaseId));
}

export function useCancelMembership(clientId: string): UseMutationResult<unknown, Error, string> {
  const { api } = usePrepaidDeps();
  return useClientWrite(clientId, (membershipId: string) =>
    api.cancelMembership(clientId, membershipId),
  );
}

/** What this bill's client could spend on it (rule 9, soonest first). */
export function useEntitlements(invoiceId: string): UseQueryResult<Credit[]> {
  const { api } = usePrepaidDeps();
  return useQuery({
    queryKey: prepaidKeys.entitlements(invoiceId),
    queryFn: () => api.readEntitlements(invoiceId),
    enabled: invoiceId !== '',
  });
}

export function useRedeemCredit(
  invoiceId: string,
): UseMutationResult<{ total_paise: number }, Error, { lineId: string; purchaseId: string }> {
  const { api } = usePrepaidDeps();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ lineId, purchaseId }) => api.redeemCredit(invoiceId, lineId, purchaseId),
    onSuccess: () => {
      // The whole namespace: a spent credit changes the bill, the client's
      // credits and — once BP-06 reprices — the invoice the till is showing.
      void client.invalidateQueries({ queryKey: prepaidKeys.all });
    },
  });
}

/** The client's own three. */
export function useMyWallet(): UseQueryResult<Wallet> {
  const { api } = usePrepaidDeps();
  return useQuery({ queryKey: [...prepaidKeys.mine(), 'wallet'], queryFn: () => api.myWallet() });
}

export function useMyCredits(): UseQueryResult<Credit[]> {
  const { api } = usePrepaidDeps();
  return useQuery({ queryKey: [...prepaidKeys.mine(), 'credits'], queryFn: () => api.myCredits() });
}

export function useMyMembership(): UseQueryResult<Membership | null> {
  const { api } = usePrepaidDeps();
  return useQuery({
    queryKey: [...prepaidKeys.mine(), 'membership'],
    queryFn: () => api.myMembership(),
  });
}
